import type { LocationQuery } from 'vue-router';

import { i18n } from '@/lang';
import { CLOUD_CHILDREN_ALL, STATIC_NAV } from '@/platform/layout/nav-config';

import { Path, type LoginStep } from './targets';

// Per-route document.title, so tabs, history and screen-reader page
// announcements can tell pages apart (WCAG 2.4.2) — index.html ships one fixed
// title, and analytics reports document.title as page_title. The page name is
// the label of the deepest entry whose path prefixes the route, so a detail
// page (/management/member/:id) reads as its section and a new nav entry titles
// itself. Pages the sidebar does not list — sign-in, errors, sub-pages with a
// purpose of their own — are named in OWN_TITLES. Only a path matching no entry
// at all keeps the bare product name.
//
// Titles are callbacks around a literal t('...') call, so the i18n tooling can
// index every key (i18n-conventions: no runtime-assembled keys).

const { t } = i18n.global;

type Title = () => string;

const OWN_TITLES: { path: string; title: Title }[] = [
  { path: Path.Notification, title: () => t('notification.title') },
  { path: '/security', title: () => t('layout.title.security') },
  { path: '/alert/create', title: () => t('layout.title.alertCreate') },
  { path: '/alert/edit', title: () => t('layout.title.alertEdit') },
  { path: '/integration/pagerduty', title: () => t('layout.title.pagerDuty') },
  { path: '/integration/ranger', title: () => t('layout.title.ranger') },
  { path: '/sign-up', title: () => t('layout.title.signUp') },
  { path: '/reset-password', title: () => t('layout.title.resetPassword') },
  { path: '/setup-password', title: () => t('layout.title.setUpAccount') },
  { path: '/mcp-authorize', title: () => t('layout.title.mcpAuthorize') },
  { path: '/404', title: () => t('layout.title.notFound') },
  { path: '/401', title: () => t('layout.title.unauthorized') },
];

const ENTRIES = [
  ...STATIC_NAV.flatMap((item) => [item, ...(item.children ?? [])]),
  ...CLOUD_CHILDREN_ALL,
]
  // Same key the sidebar renders for the entry.
  .map(({ path, langKey }) => ({ path, title: () => t(`layout.menu.${langKey}`) }))
  .concat(OWN_TITLES)
  .sort((a, b) => b.path.length - a.path.length);

// /login is one route whose `step` query swaps the whole form; each step is
// titled after what the user is doing there. `signUp` is the route's default.
const signUp: Title = () => t('layout.title.signUp');
const signIn: Title = () => t('layout.title.signIn');
const setUpAccount: Title = () => t('layout.title.setUpAccount');
const verify: Title = () => t('layout.title.verify');

const LOGIN_STEP_TITLES: Record<LoginStep, Title> = {
  signUp,
  account: signIn,
  choose: signIn,
  user: signIn,
  sso: signIn,
  setupName: setUpAccount,
  setupPassword: setUpAccount,
  invitePassword: setUpAccount,
  resetPassword: () => t('layout.title.resetPassword'),
  info: () => t('layout.title.checkEmail'),
  mfaVerify: verify,
  mfaSetup: verify,
};

function loginTitle(query: LocationQuery): string {
  const step = typeof query.step === 'string' ? query.step : 'signUp';
  return (LOGIN_STEP_TITLES[step as LoginStep] ?? signUp)();
}

export function pageTitle(path: string, query: LocationQuery = {}): string {
  const product = t('layout.name');
  if (path === '/login') return `${loginTitle(query)} | ${product}`;
  const entry = ENTRIES.find((e) => path === e.path || path.startsWith(`${e.path}/`));
  return entry ? `${entry.title()} | ${product}` : product;
}
